import { z } from "zod";
import {
  TimeEntryCategory,
  TimeEntryCategorySchema,
  TimeManagementListItem,
  CreateTimeManagementDto,
  UpdateTimeManagementDto,
} from "@myapp/contract";

const FULL_DAY_CATEGORIES: TimeEntryCategory[] = ["SICK_LEAVE", "VACATION"];

export const isFullDayCategory = (category: TimeEntryCategory): boolean =>
  FULL_DAY_CATEGORIES.includes(category);

export const timeManagementFormSchema = z
  .object({
    technicianIds: z.array(z.coerce.number().int().positive()).min(1, "Mindestens ein Handwerker"),
    category: TimeEntryCategorySchema,
    comment: z.string().default(""),
    dateFrom: z.string().default(""),
    dateTo: z.string().default(""),
    date: z.string().default(""),
    timeFrom: z.string().default(""),
    timeTo: z.string().default(""),
  })
  .superRefine((data, ctx) => {
    const isFullDay = isFullDayCategory(data.category);

    if (isFullDay) {
      if (!data.dateFrom) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Pflichtfeld", path: ["dateFrom"] });
      }
      if (!data.dateTo) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Pflichtfeld", path: ["dateTo"] });
      }
      if (data.dateFrom && data.dateTo && data.dateFrom > data.dateTo) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Von muss vor Bis liegen", path: ["dateTo"] });
      }
    } else {
      if (!data.date) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Pflichtfeld", path: ["date"] });
      }
      if (!data.timeFrom) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Pflichtfeld", path: ["timeFrom"] });
      }
      if (!data.timeTo) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Pflichtfeld", path: ["timeTo"] });
      }
      if (data.timeFrom && data.timeTo && data.timeFrom >= data.timeTo) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Von muss vor Bis liegen", path: ["timeTo"] });
      }
    }
  });

export type TimeManagementFormValues = z.infer<typeof timeManagementFormSchema>;

export function getDefaultValues(category: TimeEntryCategory): TimeManagementFormValues {
  return {
    technicianIds: [],
    category,
    comment: "",
    dateFrom: "",
    dateTo: "",
    date: "",
    timeFrom: "",
    timeTo: "",
  };
}

export function toCreateDto(values: TimeManagementFormValues, force?: boolean): CreateTimeManagementDto {
  const isFullDay = isFullDayCategory(values.category);

  if (isFullDay) {
    return {
      assignedTechnicianIds: values.technicianIds,
      category: values.category,
      comment: values.comment || undefined,
      startTime: new Date(`${values.dateFrom}T00:00:00`).toISOString(),
      endTime: new Date(`${values.dateTo}T23:59:59.999`).toISOString(),
      force,
    };
  }

  return {
    assignedTechnicianIds: values.technicianIds,
    category: values.category,
    comment: values.comment || undefined,
    startTime: new Date(`${values.date}T${values.timeFrom}:00`).toISOString(),
    endTime: new Date(`${values.date}T${values.timeTo}:00`).toISOString(),
    force,
  };
}

export function toUpdateDto(values: TimeManagementFormValues, force?: boolean): UpdateTimeManagementDto {
  const isFullDay = isFullDayCategory(values.category);

  if (isFullDay) {
    return {
      assignedTechnicianIds: values.technicianIds,
      category: values.category,
      comment: values.comment || null,
      startTime: new Date(`${values.dateFrom}T00:00:00`).toISOString(),
      endTime: new Date(`${values.dateTo}T23:59:59.999`).toISOString(),
      force,
    };
  }

  return {
    assignedTechnicianIds: values.technicianIds,
    category: values.category,
    comment: values.comment || null,
    startTime: new Date(`${values.date}T${values.timeFrom}:00`).toISOString(),
    endTime: new Date(`${values.date}T${values.timeTo}:00`).toISOString(),
    force,
  };
}

function toLocalDateString(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toLocalTimeString(iso: string): string {
  const d = new Date(iso);
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${min}`;
}

export function fromTimeManagementDetail(
  item: TimeManagementListItem,
): TimeManagementFormValues {
  const isFullDay = isFullDayCategory(item.category);
  const startDate = toLocalDateString(item.startTime);
  const endDate = toLocalDateString(item.endTime);
  const startTime = toLocalTimeString(item.startTime);
  const endTime = toLocalTimeString(item.endTime);

  return {
    technicianIds: item.technicians.map((t) => t.id),
    category: item.category,
    comment: item.comment ?? "",
    dateFrom: isFullDay ? startDate : "",
    dateTo: isFullDay ? endDate : "",
    date: isFullDay ? "" : startDate,
    timeFrom: isFullDay ? "" : startTime,
    timeTo: isFullDay ? "" : endTime,
  };
}
